import { isCollectionBlock } from '@/lib/blocks/collection/blocks';
import { FLOAT_RESTORE_POSITION_PROP } from '@/lib/blocks/layout/group-layout';
import { getBlockUiKey, isParentBlockType, type PageBlock } from '@/types/block';
import type { DropIndicator } from '../core';

type KeyboardLikeEvent = Pick<
  KeyboardEvent,
  'key' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'defaultPrevented'
>;

const isUndoShortcut = (event: KeyboardLikeEvent, normalizedKey: string): boolean =>
  (event.metaKey || event.ctrlKey) && !event.shiftKey && normalizedKey === 'z';

const isRedoShortcut = (event: KeyboardLikeEvent, normalizedKey: string): boolean =>
  (event.metaKey || event.ctrlKey) &&
  ((event.shiftKey && normalizedKey === 'z') || normalizedKey === 'y');

const isArrowBackwardKey = (key: string): boolean => key === 'ArrowLeft' || key === 'ArrowUp';
const isArrowForwardKey = (key: string): boolean => key === 'ArrowRight' || key === 'ArrowDown';

const buildStableIdByPersistedId = (blocks: Iterable<PageBlock>): Map<string, string> => {
  return new Map(Array.from(blocks).map((block) => [block.id, getBlockUiKey(block)]));
};

export const isTypingFocusElement = (activeElement: HTMLElement | null): boolean => {
  return Boolean(
    activeElement &&
      (activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.tagName === 'SELECT' ||
        activeElement.isContentEditable),
  );
};

export const resolveHistoryAction = (event: KeyboardLikeEvent): 'undo' | 'redo' | null => {
  if (event.defaultPrevented) {
    return null;
  }
  const normalizedKey = event.key.toLowerCase();
  if (isUndoShortcut(event, normalizedKey)) {
    return 'undo';
  }
  if (isRedoShortcut(event, normalizedKey)) {
    return 'redo';
  }
  return null;
};

export const shouldCancelDragOnEscape = ({
  key,
  isDragSessionActive,
}: {
  key: string;
  isDragSessionActive: boolean;
}): boolean => {
  return key === 'Escape' && isDragSessionActive;
};

export const handleDeleteSelection = ({
  key,
  isTypingFocus,
  activeSelectionStableId,
  blockMap,
}: {
  key: string;
  isTypingFocus: boolean;
  activeSelectionStableId: string | null;
  blockMap: Map<string, PageBlock>;
}): { blockId: string } | null => {
  if (key !== 'Backspace' && key !== 'Delete') {
    return null;
  }
  if (isTypingFocus || !activeSelectionStableId) {
    return null;
  }
  const activeBlock = blockMap.get(activeSelectionStableId);
  if (!activeBlock) {
    return null;
  }
  return { blockId: activeBlock.id };
};

export const resolveParentSelectionShortcut = ({
  event,
  isTypingFocus,
  activeSelectionStableId,
  blockMap,
}: {
  event: KeyboardLikeEvent;
  isTypingFocus: boolean;
  activeSelectionStableId: string | null;
  blockMap: Map<string, PageBlock>;
}): { parentStableId: string } | null => {
  const isParentSelectKey =
    (event.metaKey || event.ctrlKey) && !event.shiftKey && event.key === 'ArrowUp';
  if (!isParentSelectKey || isTypingFocus || !activeSelectionStableId) {
    return null;
  }

  const activeBlock = blockMap.get(activeSelectionStableId);
  if (!activeBlock?.parentBlockId) {
    return null;
  }

  const stableIdMap = buildStableIdByPersistedId(blockMap.values());
  const parentStableId = stableIdMap.get(activeBlock.parentBlockId);
  if (!parentStableId) {
    return null;
  }

  const parentBlock = blockMap.get(parentStableId);
  if (!parentBlock || !isParentBlockType(parentBlock.blockType)) {
    return null;
  }

  return { parentStableId };
};

const sortBlocksForParent = (
  blocks: PageBlock[],
  parentStableId: string | null,
  blockMap: Map<string, PageBlock>,
  childrenByParent: Map<string, PageBlock[]>,
  stableIdByPersistedId: Map<string, string>,
): PageBlock[] => {
  if (parentStableId !== null) {
    const parentChildren = childrenByParent.get(parentStableId);
    if (parentChildren && parentChildren.length > 0) {
      return [...parentChildren].sort((left, right) => left.position - right.position);
    }
  }

  const siblings = blocks.filter((block) => {
    const stableParentId = block.parentBlockId
      ? (stableIdByPersistedId.get(block.parentBlockId) ?? null)
      : null;
    return stableParentId === parentStableId;
  });
  if (siblings.length > 0) {
    return [...siblings].sort((left, right) => left.position - right.position);
  }

  const siblingsFromMap = Array.from(blockMap.values()).filter((block) => {
    const stableParentId = block.parentBlockId
      ? (stableIdByPersistedId.get(block.parentBlockId) ?? null)
      : null;
    return stableParentId === parentStableId;
  });
  return siblingsFromMap.sort((left, right) => left.position - right.position);
};

export function resolveKeyboardMovableBlock(
  activeStableId: string,
  blockMap: Map<string, PageBlock>,
): PageBlock | null {
  const activeBlock = blockMap.get(activeStableId);
  if (!activeBlock) {
    return null;
  }

  if (isCollectionBlock(activeBlock)) {
    return activeBlock;
  }

  return activeBlock;
}

export function reorderSiblingBlocks({
  blocks,
  siblings,
  activeStableId,
  targetStableId,
}: {
  blocks: PageBlock[];
  siblings: PageBlock[];
  activeStableId: string;
  targetStableId: string;
}): PageBlock[] | null {
  const activeIndex = siblings.findIndex((block) => getBlockUiKey(block) === activeStableId);
  const targetIndex = siblings.findIndex((block) => getBlockUiKey(block) === targetStableId);
  if (activeIndex === -1 || targetIndex === -1 || activeIndex === targetIndex) {
    return null;
  }

  const reordered = [...siblings];
  const [activeBlock] = reordered.splice(activeIndex, 1);
  if (!activeBlock) {
    return null;
  }
  const activeProps = { ...(activeBlock.props ?? {}) };
  delete activeProps[FLOAT_RESTORE_POSITION_PROP];
  const nextTargetIndex = reordered.findIndex((block) => getBlockUiKey(block) === targetStableId);
  if (nextTargetIndex === -1) {
    return null;
  }
  reordered.splice(nextTargetIndex + (activeIndex < targetIndex ? 1 : 0), 0, {
    ...activeBlock,
    props: activeProps,
  });

  const updates = new Map<string, PageBlock>();
  reordered.forEach((block, index) => {
    updates.set(block.id, { ...block, position: index + 1 });
  });

  return blocks.map((block) => updates.get(block.id) || block);
}

export const handleArrowReorder = ({
  key,
  isTypingFocus,
  activeSelectionStableId,
  blocks,
  blockMap,
  childrenByParent,
}: {
  key: string;
  isTypingFocus: boolean;
  activeSelectionStableId: string | null;
  blocks: PageBlock[];
  blockMap: Map<string, PageBlock>;
  childrenByParent: Map<string, PageBlock[]>;
}): { nextBlocks: PageBlock[]; indicator: DropIndicator; movedStableId: string } | null => {
  const isBackwardKey = isArrowBackwardKey(key);
  const isForwardKey = isArrowForwardKey(key);
  if (!isBackwardKey && !isForwardKey) {
    return null;
  }
  if (isTypingFocus || !activeSelectionStableId) {
    return null;
  }

  const resolvedStableIdByPersistedId = buildStableIdByPersistedId(blocks);
  const activeBlock = resolveKeyboardMovableBlock(activeSelectionStableId, blockMap);
  if (!activeBlock) {
    return null;
  }

  const activeParentId = activeBlock.parentBlockId
    ? (resolvedStableIdByPersistedId.get(activeBlock.parentBlockId) ?? null)
    : null;
  const siblings = sortBlocksForParent(
    blocks,
    activeParentId,
    blockMap,
    childrenByParent,
    resolvedStableIdByPersistedId,
  );
  const activeIndex = siblings.findIndex(
    (sibling) => getBlockUiKey(sibling) === getBlockUiKey(activeBlock),
  );
  if (activeIndex === -1) {
    return null;
  }

  const targetIndex = isBackwardKey ? activeIndex - 1 : activeIndex + 1;
  const targetBlock = siblings[targetIndex];
  if (!targetBlock) {
    return null;
  }

  const indicator: DropIndicator = {
    targetId: getBlockUiKey(targetBlock),
    edge: isBackwardKey ? 'top' : 'bottom',
  };

  const nextBlocks = reorderSiblingBlocks({
    blocks,
    siblings,
    activeStableId: getBlockUiKey(activeBlock),
    targetStableId: indicator.targetId,
  });
  if (!nextBlocks) {
    return null;
  }

  return {
    nextBlocks,
    indicator,
    movedStableId: getBlockUiKey(activeBlock),
  };
};
